import type { FunctionComponent, VoidFunctionComponent } from 'react';

import { window } from '../../../globals';
import COLORS from '../../../theme/colors';
import utils from '../../../utils';
import { getFeatureDetails } from '../../settings/billing/features/consts';
import { BUTTON_SIZE, BUTTON_TYPE } from '../Button';
import NavigationButton from '../Button/NavigationButton';
import Icon, { icons } from '../Icon';
import { SupportLink } from '../Link';
import type { ACCESS_LEVEL_TO_PLAN } from '../../../../shared/subscriptions';
import type { FeatureDescription, FossaFeature } from '../../settings/billing/features/consts';

import styles from './InvalidAccess.module.less';

const SimpleLockMessage: VoidFunctionComponent = () => (
  <div>
    <h3 style={{ color: COLORS.fossaRed }}>
      <Icon icon={icons.lock} /> This feature is locked for your subscription
    </h3>
    <p style={{ marginLeft: '45px' }}>
      {' '}
      For questions, please contact <SupportLink>support@fossa.com</SupportLink>
    </p>
  </div>
);

const FeatureLockMessage: VoidFunctionComponent<{ focusFeature: FeatureDescription }> = ({ focusFeature }) => {
  return (
    <div>
      <h3 style={{ color: COLORS.fossaRed, paddingTop: '5px' }}>
        <b>{focusFeature.name}</b> {focusFeature.isFeatureNamePlural ? 'are' : 'is'} not available with your plan.
      </h3>
      <br />
      {focusFeature.tooltipText && (
        <p>
          {' '}
          <b>Upgrade to unlock:</b> {focusFeature.tooltipText}
        </p>
      )}
    </div>
  );
};

const LoginToAccess: VoidFunctionComponent = () => {
  return (
    <div className="card card-primary text-center">
      <h2>Login to Access</h2>
      <p>This feature requires login to access</p>
      <a href="/account/github_login" className="btn btn-primary">
        <Icon icon={icons.github} /> Login with GitHub
      </a>
      <a href="/auth/login" className="btn btn-link">
        Login with Email
      </a>
    </div>
  );
};

type InvalidAccessProps = {
  isFullPage: boolean;
  focusFeature?: FossaFeature;
  accessLevel: keyof typeof ACCESS_LEVEL_TO_PLAN;
};

const InvalidAccess: FunctionComponent<InvalidAccessProps> = ({ focusFeature, isFullPage, accessLevel, children }) => {
  if (!window.user) {
    return <LoginToAccess />;
  }

  const featureDetails = getFeatureDetails(focusFeature);

  return (
    <div className={isFullPage ? 'panel full-page-access' : ''}>
      <div className={isFullPage ? styles.navigationButtonFullPage : styles.navigationButton}>
        <NavigationButton size={BUTTON_SIZE.MEDIUM} type={BUTTON_TYPE.SECONDARY} to="/account/settings/billing">
          Upgrade to {utils.capitalizeWord(accessLevel)}
        </NavigationButton>
      </div>
      {featureDetails ? <FeatureLockMessage focusFeature={featureDetails} /> : <SimpleLockMessage />}

      {children || null}
    </div>
  );
};

export default InvalidAccess;
